import {createContext, useContext} from 'react'
import PropTypes from 'prop-types'
import {useSimpleQuery} from '@frnt/base/hooks'
import {ResourceApiServiceProvider} from '../resource-api-service-provider'
import qs from 'qs'

const ArticlesServiceContext = createContext(undefined)

const ArticlesServiceProvider = ({
  children,
  baseApiHost,
  handleUnauthorized = () => {
    // Do nothing
  }
}) => {
  return (
    <ResourceApiServiceProvider
      Context={ArticlesServiceContext}
      handleUnauthorized={handleUnauthorized}
      host={baseApiHost}
      valueCallback={(axios) => ({
        create: async (payload) => {
          const {data} = await axios.post(`/api/v1/deta_bot/articles`, payload)
          return data
        },
        get: async (id) => {
          const {data} = await axios.get(`/api/v1/deta_bot/articles/${id}`)
          return data
        },
        list: async (query) => {
          const {data} = await axios.get(
            `/api/v1/deta_bot/articles${qs.stringify(query, {
              addQueryPrefix: true
            })}`
          )
          return data
        },
        update: async (id, payload) => {
          const {data} = await axios.patch(`/api/v1/deta_bot/articles/${id}`, payload)
          return data
        },
        listRecent: async (queryParams = null) => {
          const {data} = await axios.get(
            `/api/v1/deta_bot/articles/recent${qs.stringify(
              {
                ...queryParams
              },
              {addQueryPrefix: true}
            )}`
          )
          return data
        }
      })}>
      {children}
    </ResourceApiServiceProvider>
  )
}

ArticlesServiceProvider.propTypes = {
  children: PropTypes.any,
  baseApiHost: PropTypes.string.isRequired,
  handleUnauthorized: PropTypes.func
}

const useArticlesService = () => {
  const context = useContext(ArticlesServiceContext)
  if (context === undefined) {
    throw new Error('ArticlesServiceContext was not provided. Make sure your component is a child of the ArticlesServiceProvider.')
  }
  return context
}

const useArticlesQuery = ({query, ...otherProps}) => {
  const articlesService = useArticlesService()
  return useSimpleQuery({
    queryKey: `articles-list-${JSON.stringify(query)}`,
    queryFn: async () => articlesService.list(query),
    ...otherProps
  })
}

const useArticleQuery = ({id, enabled, ...otherProps}) => {
  const articlesService = useArticlesService()
  return useSimpleQuery({
    queryKey: `articles-get-${id}`,
    queryFn: async () => articlesService.get(id),
    enabled: enabled !== undefined ? enabled : !!id,
    ...otherProps
  })
}

const useRecentArticlesQuery = ({page, ...otherProps}) => {
  const articlesService = useArticlesService()
  return useSimpleQuery({
    queryKey: `recent-articles-${page}`,
    queryFn: async () =>
      articlesService.listRecent({
        page,
        pageSize: 10
      }),
    ...otherProps
  })
}

export {ArticlesServiceProvider, useArticlesService, useArticlesQuery, useArticleQuery, useRecentArticlesQuery}
